import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@carbon/react';
import { Rocket } from '@carbon/react/icons';
import { useAtom } from 'jotai';
import { useState } from 'react';

import { SuccessToast } from 'components/success-toast/success-toast';

import {
  promoteModalOpenAtom,
  promoteWorkspaceLoadableAtom,
  slotListAtomFamily,
} from './atoms';
import { SlotStatusTable } from './slot-status-table/slot-status-table';
import { getMutationErrorMessage } from './utils';
import { useWorkspaceConfirmation } from './workspace-confirmation-context';
import styles from './promote-modal.module.scss';

/**
 * Promote Workspace modal. Confirms the user wants to flip the workspace to its
 * read-only "Promoted" state and fires the BE POST. The four mutation surfaces
 * (per-row Confirm, bulk Confirm, Auto-Confirm, Promote button) gate themselves
 * off the post-success workspace refetch — no orchestration needed here beyond
 * closing the modal + surfacing the toast.
 *
 * @return {JSX.Element} The rendered modal (hidden when closed).
 */
export const PromoteModal = (): JSX.Element => {
  const [open, setOpen] = useAtom(promoteModalOpenAtom);
  const { workspaceId } = useWorkspaceConfirmation();
  const [loadable, promote] = useAtom(promoteWorkspaceLoadableAtom);
  const [error, setError] = useState<string | null>(null);
  // Decoupled from the modal session so the toast survives the modal closing.
  const [successToastVisible, setSuccessToastVisible] = useState(false);

  const loading = loadable.state === 'loading';

  const handleClose = (): void => {
    if (loading) return;
    setOpen(false);
    setError(null);
  };

  const handlePromote = async (): Promise<void> => {
    if (loading) return;
    setError(null);
    try {
      await promote(workspaceId);
      setOpen(false);
      setSuccessToastVisible(true);
    } catch (err) {
      setError(getMutationErrorMessage(err, "Couldn't promote workspace."));
    }
  };

  return (
    <>
      <ComposedModal
        open={open}
        onClose={handleClose}
        preventCloseOnClickOutside
        size="lg"
      >
        <ModalHeader title="Promote Workspace" />
        <ModalBody>
          <p className={styles.intro}>
            Promoting these values gives them certified status across the
            system. Certified values are approved for use in downstream
            workflows and published outputs.
          </p>
          <p className={styles.intro}>
            After promotion, this workspace becomes a read-only record of the
            completed work. Certified values can&apos;t be edited directly. To
            change them, create a new workspace, update the values, and promote
            them again.
          </p>
          <h3 className={styles.slotCount}>Tracts you changed this session</h3>
          <div className={styles.slotTableViewport}>
            <SlotStatusTable
              searchAtom={slotListAtomFamily({
                surface: 'promote',
                workspaceId,
              })}
              workspaceId={workspaceId}
              changedOnly
            />
          </div>
          {error && (
            <InlineNotification
              className={styles.notification}
              kind="error"
              lowContrast
              hideCloseButton
              title={error}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            kind="primary"
            renderIcon={Rocket}
            disabled={loading}
            onClick={handlePromote}
          >
            {loading ? 'Promoting…' : 'Promote'}
          </Button>
        </ModalFooter>
      </ComposedModal>
      {successToastVisible && (
        <SuccessToast
          message="Workspace promoted"
          onClose={() => {
            setSuccessToastVisible(false);
            return false;
          }}
        />
      )}
    </>
  );
};
